import { roleManagementService, type AppKnowledgeAreaGrant, type UserPermissions } from './role-management-service'
import { getCurrentUserId } from '@/services/auth-service'
import { myAppsService } from '@/services/my-apps-service'
import type { ExternalOpenMode } from '@/services/backend-apps-service'
import { apiRequest } from '@/lib/api-client'
import { logError } from '@/lib/logger'

export interface Client {
  id: string
  name: string
  default_theme_id: string | null
  created_at: string
  updated_at: string
}

export interface UserProfile {
  id: string
  role: 'admin' | 'user'
  is_active: boolean
  is_readonly?: boolean // Deprecated - kept for backward compatibility with database
  approved_at?: string
  approved_by?: string
  client_id: string
  client_name?: string
  created_at: string
  updated_at: string
  email?: string
  name?: string | null
  allowed_applications: string[]
  /** The products of the user's organisation (#1586), the only ones that can be granted; null without one. */
  organisation_applications?: string[] | null
  auto_verify_tl1: boolean
}

export interface AccessRequest {
  id: string
  user_id?: string
  email: string
  name: string
  message?: string
  status: 'pending' | 'approved' | 'rejected'
  created_at: string
  updated_at: string
  approved_by?: string
  approved_at?: string
}

// How an app relates to knowledge areas (cdb_tqs#936). 'write' apps are what
// make a knowledge area writable for a role — an area granted only to 'read'
// apps is read-only in effect.
export interface AvailableApp {
  id: string
  name: string
  display_name: string
  description?: string
  icon?: string
  category?: string
  is_active: boolean
  external_url?: string
  open_mode?: ExternalOpenMode
  knowledge_area_aware: boolean
  created_at: string
  updated_at: string
}

export interface KnowledgeArea {
  id: string
  name: string
  display_name: string
  description?: string
  bucket_name: string
  client_id?: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface UserKnowledgeAreaAssignment {
  id: string
  user_id: string
  app_id: string
  knowledge_area_id: string
  /** Knowledge Verifier: verify (true) or read only (false), #1674. */
  can_verify?: boolean
  assigned_by?: string
  assigned_at: string
  knowledge_area?: KnowledgeArea
}

export interface CreateAccessRequestData {
  email: string
  name: string
  message?: string
}

export interface ClientAppVisibility {
  id: string
  client_id: string
  app_id: string
  client_name?: string
  app_name?: string
  created_at: string
}

// The admin panel's frontend app id / route is "cortex" (issue #200), but the
// BFF/DB app record is still named "admin". Translate frontend ids to backend
// names at the access-check boundary so permission checks stay correct no matter
// which call path reaches them.
const BACKEND_APP_NAME_ALIASES: Record<string, string> = { cortex: 'admin' }

export const userManagementService = {
  // Clients
  async getClients(): Promise<Client[]> {
    const result = await apiRequest<Client[]>('/user-management/clients')
    return result || []
  },

  // Zugangsanfragen
  async createAccessRequest(data: CreateAccessRequestData): Promise<AccessRequest> {
    const result = await apiRequest<AccessRequest>('/user-management/access-requests', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    if (!result) throw new Error('Fehler beim Erstellen der Zugangsanfrage')
    return result
  },

  async getAccessRequests(): Promise<AccessRequest[]> {
    const result = await apiRequest<AccessRequest[]>('/user-management/access-requests')
    return result || []
  },

  async approveAccessRequest(requestId: string, clientId: string): Promise<void> {
    await apiRequest(`/user-management/access-requests/${requestId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ client_id: clientId }),
    })
  },

  async rejectAccessRequest(requestId: string): Promise<void> {
    await apiRequest(`/user-management/access-requests/${requestId}/reject`, {
      method: 'POST',
    })
  },

  // Benutzerprofile
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    const profile = await apiRequest<UserProfile>(`/user-management/profiles/${userId}`)
    if (!profile) return null
    return {
      ...profile,
      client_id: profile.client_id || '',
      auto_verify_tl1: profile.auto_verify_tl1 ?? false,
    }
  },

  async getAllUserProfiles(): Promise<UserProfile[]> {
    const profiles = (await apiRequest<UserProfile[]>('/user-management/profiles')) || []
    return profiles.map(p => ({
      ...p,
      client_id: p.client_id || '',
      auto_verify_tl1: p.auto_verify_tl1 ?? false,
    }))
  },

  async updateUserRole(userId: string, role: 'admin' | 'user'): Promise<void> {
    await apiRequest(`/user-management/profiles/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    })
  },

  async updateUserClient(userId: string, clientId: string): Promise<void> {
    await apiRequest(`/user-management/profiles/${userId}/client`, {
      method: 'PUT',
      body: JSON.stringify({ client_id: clientId }),
    })
  },

  async updateAllowedApplications(userId: string, allowedApplications: string[]): Promise<void> {
    await apiRequest(`/user-management/profiles/${userId}/allowed-applications`, {
      method: 'PUT',
      body: JSON.stringify({ allowed_applications: allowedApplications }),
    })
  },

  async deleteUser(userId: string): Promise<void> {
    await apiRequest(`/user-management/profiles/${userId}`, {
      method: 'DELETE',
    })
  },

  // Apps - using backend API (external_url enriched from the app_external_urls table via BFF)
  async getAvailableApps(): Promise<AvailableApp[]> {
    const { backendAppsService } = await import('./backend-apps-service')
    const { externalUrlAppsService } = await import('./external-url-apps-service')
    const [apps, externalUrls] = await Promise.all([
      backendAppsService.getActiveApps(),
      externalUrlAppsService.getAll(),
    ])
    const urlMap = new Map(externalUrls.map((e) => [e.app_name, e.external_url]))
    return apps.map(app => ({
      id: app.id,
      name: app.name,
      display_name: app.display_name,
      description: app.description ?? undefined,
      icon: app.icon ?? undefined,
      category: app.category,
      is_active: app.is_active,
      knowledge_area_aware: app.knowledge_area_aware,
      external_url: urlMap.get(app.name),
      open_mode: app.open_mode ?? undefined,
      created_at: app.created_at,
      updated_at: app.updated_at,
    }))
  },

  async getAllApps(): Promise<AvailableApp[]> {
    const { backendAppsService } = await import('./backend-apps-service')
    const { externalUrlAppsService } = await import('./external-url-apps-service')
    const [apps, externalUrls] = await Promise.all([
      backendAppsService.getApps(),
      externalUrlAppsService.getAll(),
    ])
    const urlMap = new Map(externalUrls.map((e) => [e.app_name, e.external_url]))
    return apps.map(app => ({
      id: app.id,
      name: app.name,
      display_name: app.display_name,
      description: app.description ?? undefined,
      icon: app.icon ?? undefined,
      category: app.category,
      is_active: app.is_active,
      knowledge_area_aware: app.knowledge_area_aware,
      external_url: urlMap.get(app.name),
      open_mode: app.open_mode ?? undefined,
      created_at: app.created_at,
      updated_at: app.updated_at,
    }))
  },

  async createApp(app: Omit<AvailableApp, 'id' | 'created_at' | 'updated_at'>): Promise<AvailableApp> {
    const { backendAppsService } = await import('./backend-apps-service')
    // BFF handles app_external_urls upsert when external_url is provided
    const created = await backendAppsService.createApp({
      name: app.name,
      display_name: app.display_name,
      description: app.description,
      icon: app.icon,
      category: app.category,
      is_active: app.is_active,
      external_url: app.external_url,
      open_mode: app.open_mode,
    })
    if (!created) throw new Error('Fehler beim Erstellen der App')
    return {
      id: created.id,
      name: created.name,
      display_name: created.display_name,
      description: created.description ?? undefined,
      icon: created.icon ?? undefined,
      category: created.category,
      is_active: created.is_active,
      knowledge_area_aware: created.knowledge_area_aware,
      external_url: app.external_url,
      open_mode: created.open_mode ?? undefined,
      created_at: created.created_at,
      updated_at: created.updated_at,
    }
  },

  async updateApp(appId: string, updates: Partial<AvailableApp>): Promise<void> {
    const { backendAppsService } = await import('./backend-apps-service')
    // BFF handles app_external_urls upsert/delete/rename when external_url is included
    await backendAppsService.updateApp(appId, {
      name: updates.name,
      display_name: updates.display_name,
      description: updates.description,
      icon: updates.icon,
      category: updates.category,
      is_active: updates.is_active,
      knowledge_area_aware: updates.knowledge_area_aware,
      // Only include external_url if it was explicitly part of the update
      ...('external_url' in updates ? { external_url: updates.external_url ?? null } : {}),
      open_mode: updates.open_mode,
    })
  },

  async deleteApp(appId: string, _appName?: string): Promise<void> {
    const { backendAppsService } = await import('./backend-apps-service')
    // BFF cascades role_app_assignments and app_external_urls cleanup internally
    await backendAppsService.deleteApp(appId)
  },

  async getAllAvailableAppsForUser(userId: string): Promise<AvailableApp[]> {
    const isAdmin = await this.isUserAdmin(userId)
    const isActive = await this.isUserActive(userId)

    if (!isActive) return []

    const allApps = await this.getAllApps()

    // Check if this is for the current user (use /me endpoint) or another user (admin only)
    const currentUserId = getCurrentUserId()
    const isOwnUser = currentUserId === userId

    // Get user permissions via roles. Same reasoning as above: degrade to "no
    // apps" instead of throwing at the desktop.
    let permissions: UserPermissions
    try {
      permissions = isOwnUser
        ? await roleManagementService.getMyPermissions()
        : await roleManagementService.getUserPermissions(userId)
    } catch (error) {
      logError(error as Error, { context: 'UserManagementService', method: 'getAllAvailableAppsForUser' })
      return []
    }

    return allApps.filter(app => {
      // System-Apps sind immer verfügbar
      if (app.name === 'settings' || app.name === 'app-store') {
        return true
      }

      // Admin-Apps sind nur für Admins verfügbar.
      // NB: Das Frontend-App-Label/-ID heißt "cortex" (issue #200), der
      // Backend-/DB-App-Datensatz bleibt aber bewusst "admin" — hier wird der
      // Backend-Name geprüft, nicht die Frontend-App-ID.
      if (app.name === 'admin') {
        return isAdmin
      }

      // Admins haben Zugriff auf alle Apps
      if (isAdmin) return true

      // Normale Apps: Prüfe über Rollen-Permissions
      return permissions.app_ids.includes(app.id)
    })
  },

  // Hilfsfunktionen
  async isUserAdmin(userId: string): Promise<boolean> {
    const result = await apiRequest<boolean>(`/user-management/profiles/${userId}/is-admin`)
    return result === true
  },

  async isUserActive(userId: string): Promise<boolean> {
    const result = await apiRequest<boolean>(`/user-management/profiles/${userId}/is-active`)
    return result === true
  },

  async canUserAccessApp(userId: string, appName: string): Promise<boolean> {
    const backendName = BACKEND_APP_NAME_ALIASES[appName] ?? appName
    // The own user's answer is the cached /apps/me list (#1610). Cortex's
    // record "admin" is not a listed grant, so the server decides it.
    if (userId === getCurrentUserId() && backendName !== 'admin') {
      const { isSystemApp } = await import('@/data/apps')
      if (isSystemApp(appName)) return true
      return myAppsService.hasApp(backendName)
    }
    const result = await apiRequest<boolean>(`/user-management/users/${userId}/can-access-app/${backendName}`)
    return result === true
  },

  async hasActiveRequest(userId: string): Promise<boolean> {
    const activeRequest = await this.getActiveRequestForUser(userId)
    return !!activeRequest
  },

  async getActiveRequestForUser(userId: string): Promise<AccessRequest | null> {
    const requests = await this.getAccessRequests()
    return requests.find(r => r.user_id === userId && r.status === 'pending') || null
  },

  // Knowledge Areas
  /**
   * @param includeInactive Surfaces deactivated areas as well. Permission
   *   surfaces need this: cdb_tqs still grants access to a deactivated area
   *   (its client filter checks existence and client, never is_active), so an
   *   admin who cannot see one cannot revoke it either (cdb_webos#664).
   *   Everything that merely lists or counts areas leaves this off.
   */
  async getAllKnowledgeAreas(includeInactive = false): Promise<KnowledgeArea[]> {
    try {
      // client_id via CDB-TQS API
      const { cdbTqsApiService } = await import('@/services/cdb-tqs-api-service')
      const response = await cdbTqsApiService.getKnowledgeAreas(!includeInactive)

      // Map CDB-TQS response to KnowledgeArea interface
      return response.knowledge_areas.map(ka => ({
        id: ka.id,
        name: ka.name,
        display_name: ka.display_name,
        description: ka.description || undefined,
        bucket_name: `knowledge-area-${ka.name}`,
        client_id: ka.client_id || null,
        is_active: ka.is_active,
        created_at: ka.created_at,
        updated_at: ka.updated_at
      }))
    } catch (error) {
      console.warn('Knowledge Areas Funktion fehlgeschlagen:', error)
      return []
    }
  },

  async getUserKnowledgeAreaAssignments(userId: string): Promise<UserKnowledgeAreaAssignment[]> {
    const result = await apiRequest<UserKnowledgeAreaAssignment[]>(`/user-management/users/${userId}/knowledge-area-assignments`)
    return result || []
  },

  async getAllKnowledgeAreaAssignments(): Promise<UserKnowledgeAreaAssignment[]> {
    const result = await apiRequest<UserKnowledgeAreaAssignment[]>('/user-management/knowledge-area-assignments')
    return result || []
  },

  async assignKnowledgeAreaToUser(userId: string, appId: string, knowledgeAreaId: string): Promise<void> {
    await apiRequest(`/user-management/users/${userId}/knowledge-area-assignments`, {
      method: 'POST',
      body: JSON.stringify({ app_id: appId, knowledge_area_id: knowledgeAreaId }),
    })
  },

  /** Replace a user's direct, per-app knowledge area grants in one call (#936). */
  async setUserAppKnowledgeAreas(
    userId: string,
    grants: AppKnowledgeAreaGrant[],
  ): Promise<void> {
    await apiRequest(`/user-management/users/${userId}/knowledge-area-assignments`, {
      method: 'PUT',
      body: JSON.stringify({ app_knowledge_areas: grants }),
    })
  },

  async removeKnowledgeAreaFromUser(userId: string, appId: string, knowledgeAreaId: string): Promise<void> {
    await apiRequest(`/user-management/users/${userId}/knowledge-area-assignments/${appId}/${knowledgeAreaId}`, {
      method: 'DELETE',
    })
  },

  async getAllAvailableKnowledgeAreasForUser(userId: string): Promise<KnowledgeArea[]> {
    try {
      const isAdmin = await this.isUserAdmin(userId)
      const isActive = await this.isUserActive(userId)

      if (!isActive) return []

      const allKnowledgeAreas = await this.getAllKnowledgeAreas()

      // Admins haben Zugriff auf alle Knowledge Areas
      if (isAdmin) return allKnowledgeAreas

      // Check if this is for the current user (use /me endpoint) or another user (admin only)
      const currentUserId = getCurrentUserId()
      const isOwnUser = currentUserId === userId

      // Normale Benutzer: Prüfe über Rollen-Permissions
      // Use getMyPermissions for own user (no admin required), getUserPermissions for others (admin only)
      const permissions = isOwnUser
        ? await roleManagementService.getMyPermissions()
        : await roleManagementService.getUserPermissions(userId)

      return allKnowledgeAreas.filter(ka => permissions.knowledge_area_ids.includes(ka.id))
    } catch (error) {
      console.warn('getAllAvailableKnowledgeAreasForUser fehlgeschlagen:', error)
      return []
    }
  },

  async setUserAutoVerifyTl1(userId: string, enabled: boolean): Promise<void> {
    await apiRequest(`/user-management/profiles/${userId}/auto-verify-tl1`, {
      method: 'PUT',
      body: JSON.stringify({ auto_verify_tl1: enabled }),
    })
  },

  // Client App Visibility
  async getAllClientAppVisibility(): Promise<ClientAppVisibility[]> {
    const result = await apiRequest<ClientAppVisibility[]>('/user-management/client-app-visibility')
    return result || []
  },

  async getClientAppVisibility(clientId: string): Promise<ClientAppVisibility[]> {
    const result = await apiRequest<ClientAppVisibility[]>(`/user-management/client-app-visibility/${clientId}`)
    return result || []
  },

  async createClientAppVisibility(clientId: string, appId: string): Promise<ClientAppVisibility> {
    const result = await apiRequest<ClientAppVisibility>('/user-management/client-app-visibility', {
      method: 'POST',
      body: JSON.stringify({ client_id: clientId, app_id: appId }),
    })
    if (!result) throw new Error('Fehler beim Erstellen der Client-App-Zuordnung')
    return result
  },

  async deleteClientAppVisibility(visibilityId: string): Promise<void> {
    await apiRequest(`/user-management/client-app-visibility/${visibilityId}`, {
      method: 'DELETE',
    })
  },

  async getAppsForClient(clientId: string): Promise<AvailableApp[]> {
    const result = await apiRequest<AvailableApp[]>(`/user-management/apps-for-client/${clientId}`)
    return result || []
  },
}
